import { Alert, Button, Card, Checkbox, Col, ConfigProvider, DatePicker, Divider, Form, Input, InputNumber, Modal, Row, Segmented, Select, Space, Statistic, Typography } from "antd";
import { rightsService } from "../../../../services/rightsService";
import MembrePersonnelCheck from "../../../../components/MembrePersonnelCheck";
import SDIModal from "../../../../components/ui/SDIModal";
import useEnginReferentiels from "../../../../hooks/useEnginReferentiels";
import { MarqueSelect, TypeAutoComplete } from "../../../../components/engin/MarqueTypeFields";
import { coerceToCategorie } from "../../../../components/engin/categorieRules";
import PropTypes from "prop-types";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../../../variables/api/axiosInstance";
import AvenantAgencySelector from "./AvenantAgencySelector";
import { applyAvenantAgenceCommission } from "../../../../services/avenantCommissionService";
import FlotteExcelImport from "../AddContrat/FlotteExcelImport";
import { matchGarantieNamesToIds } from "../AddContrat/importEnginsHelpers";

const { Text, Title } = Typography;
const { Option } = Select;

const normalizeConducteurHabituelPayload = (conducteur) => {
    if (!conducteur) return null;
    const nom = (conducteur.nom || "").trim();
    const prenom = (conducteur.prenom || "").trim();
    const adresse = (conducteur.adresse || "").trim();
    const telephone = (conducteur.telephone || "").replace(/\s/g, "");

    if (!nom || !prenom || !adresse || !telephone) {
        return null;
    }

    const { telephone: _rawTelephone, contact = [], ...rest } = conducteur;
    const contacts = telephone
        ? [telephone]
        : Array.isArray(contact)
            ? contact.filter(Boolean)
            : [];

    return {
        ...rest,
        contact: contacts,
    };
};

const Incorporation = (props) => {
    const [modalIncorporation, setModalIncorporation] = useState(props.open);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    // États pour les données du contrat et de l'incorporation
    const [contratFlotte, setContratFlotte] = useState(null);
    // Plancher de date d'effet (dernier AN/RN de la police) ; chargé depuis le backend à l'ouverture.
    const [plancherDate, setPlancherDate] = useState(null);
    const [dateEffetIncorporation, setDateEffetIncorporation] = useState(dayjs());
    const [joursRestants, setJoursRestants] = useState(0);
    const [preview, setPreview] = useState(null);
    const [fraisAccessoires, setFraisAccessoires] = useState(0);
    const [valeurVenaleMinimale, setValeurVenaleMinimale] = useState(0);
    // Agence ciblée + taux (siège) à appliquer au contrat résultant de l'avenant
    const [avCom, setAvCom] = useState({ agence: null, taux: null, intermediaire: null });

    // Bonus/Malus édité (HCO080) — opt-in : appliqué seulement s'il est saisi.
    // Signé : positif = bonus (réduction), négatif = malus (majoration).
    const canEditBonus = rightsService("HCO080");
    const canMarkPersonnel = rightsService("HCO090");
    // Antidatage de l'incorporation : sous le droit HCO081, la date d'effet peut remonter dans le
    // passé — MAIS jamais avant la date d'effet de l'avenant EN COURS (dernière version du contrat).
    const canAntidate = rightsService("HCO081");
    // Plafond du bonus (%), PARAMÉTRABLE côté serveur (table Parametre) ; 30 % par défaut.
    const [BONUS_MAX, setBonusMax] = useState(30);
    // Plafond du bonus « membre du personnel » (séparé, défaut 50 %).
    const [BONUS_MAX_PERSONNEL, setBonusMaxPersonnel] = useState(50);
    useEffect(() => {
        axiosInstance.get('/api/parametre/plafond-bonus')
            .then((res) => {
                const v = Number(res.data);
                if (!Number.isNaN(v) && v > 0) setBonusMax(v);
            })
            .catch(() => { /* défaut 35 conservé */ });
        axiosInstance.get('/api/parametre/plafond-bonus-personnel')
            .then((res) => {
                const v = Number(res.data);
                if (!Number.isNaN(v) && v > 0) setBonusMaxPersonnel(v);
            })
            .catch(() => { /* défaut 50 conservé */ });
    }, []);
    const [membrePersonnel, setMembrePersonnel] = useState(!!props?.contrat?.membrePersonnel);
    const [bonusMalusEdite, setBonusMalusEdite] = useState(null);
    const [bonusMalusSens, setBonusMalusSens] = useState("BONUS");
    const effectiveBonusMax = membrePersonnel ? BONUS_MAX_PERSONNEL : BONUS_MAX;
    const bonusMalusAbs = Math.abs(Number(bonusMalusEdite) || 0);
    const applyBonusSens = (sens, magnitude) => {
        setBonusMalusSens(sens);
        let a = Math.abs(Number(magnitude) || 0);
        if (sens === "BONUS" && a > effectiveBonusMax) a = effectiveBonusMax;
        setBonusMalusEdite(a === 0 ? null : (sens === "MALUS" ? -a : a));
    };
    const handleMembrePersonnel = (checked, max) => {
        setMembrePersonnel(checked);
        if (checked) { setBonusMalusSens("BONUS"); setBonusMalusEdite(max || BONUS_MAX_PERSONNEL); }
        else { setBonusMalusEdite(null); }
    };
    const bonusMalusPayload = ((canEditBonus || membrePersonnel) && bonusMalusEdite != null && bonusMalusEdite !== "")
        ? Number(bonusMalusEdite) : null;

    // États pour les engins à incorporer
    const [enginsData, setEnginsData] = useState([{
        // Informations engin
        categorie: 'MOTO',
        matricule: '',
        typeSerie: 'TG',
        numeroSerie: '',
        marque: '',
        energie: 'ESSENCE',
        volume: '',
        nombrePlace: '',
        usage: 'PRIVE',
        moteur: '',
        type: '',
        // Pas de défaut MOTO en dur : null hors moto (jamais « VELOMOTEUR » sur un non-moto).
        genre: null,
        numeroChassis: '',
        valeurVenale: '',
        valeurANeuf: '',
        valeurDeclaree: '',
        dateMiseEnCirculation: null,
        carteGrise: null,

        // Conducteur habituel (optionnel)
        conducteurHabituel: null,
        hasConducteurDifferent: false,

        // Garanties
        garantiesSupplementaires: []
    }]);

    // Import Excel des engins à incorporer (uniquement les NOUVEAUX engins, pas de diff).
    const [showImportModal, setShowImportModal] = useState(false);

    // Modal pour le conducteur habituel
    const [modalConducteur, setModalConducteur] = useState({
        show: false,
        enginIndex: null,
        data: {
            nom: '',
            prenom: '',
            genre: 'MASCULIN',
            profession: '',
            email: '',
            adresse: '',
            telephone: '',
            typePiece: '',
            numIdentite: '',
            dateDelivrance: ''
        }
    });

    // Données de référence
    // Référentiels engin (source unique partagée) : genre/carrosserie filtrés par la CATÉGORIE
    // du contrat (repli global + dédup), et marques /api/vehicule-marques portant les MODÈLES.
    // Remplace : l'ancien endpoint MARQUE filtré en dur sur "MOTO" (liste fausse hors moto),
    // le genre sans repli, et la carrosserie qui était TOTALEMENT absente de cet écran.
    const catCodeRef = props?.contrat?.engins?.[0]?.categorie?.codeCategorie?.trim();
    const { genres, carosseries, marques } = useEnginReferentiels(catCodeRef);

    // Toute valeur de genre/carrosserie absente de la liste de la catégorie est remplacée par
    // l'élément par défaut de cette catégorie (valeurs héritées d'une autre catégorie).
    useEffect(() => {
        if (!genres.length && !carosseries.length) return;
        setEnginsData((prev) => {
            let changed = false;
            const next = (prev || []).map((e) => {
                const genre = coerceToCategorie(e?.genre, genres);
                const carosserie = coerceToCategorie(e?.carosserie, carosseries);
                if (genre === e?.genre && carosserie === e?.carosserie) return e;
                changed = true;
                return { ...e, genre, carosserie };
            });
            return changed ? next : prev;
        });
    }, [genres, carosseries]);
    const [garantiesNonObligatoires, setGarantiesNonObligatoires] = useState([]);
    const [professions, setProfessions] = useState([]);

    // Mode édition : on modifie une proposition d'incorporation déjà enregistrée
    // (IN + EN_PROPOSITION). On remplace l'ensemble des engins incorporés.
    const isEditProposition =
        props?.contrat?.statutContrat === "EN_PROPOSITION" && props?.contrat?.avenant?.code === "IN";

    // Reconstitue une ligne du formulaire à partir d'un engin déjà incorporé.
    // L'immatriculation est replacée telle quelle dans « matricule » (typeSerie/numeroSerie
    // vides) → à la soumission immatriculation = "" + "" + matricule = immatriculation d'origine.
    const mapIncorporeToEnginData = (e) => ({
        categorie: props?.contrat?.engins?.[0]?.categorie?.nom || 'MOTO',
        matricule: e?.immatriculation || '',
        typeSerie: '',
        numeroSerie: '',
        marque: e?.marque || '',
        energie: e?.energie || 'ESSENCE',
        volume: e?.volume ?? e?.puissance ?? '',
        nombrePlace: e?.nombrePlace ?? '',
        usage: e?.usage || 'PRIVE',
        moteur: e?.moteur || '',
        type: e?.type || '',
        genre: e?.genre || null,
        numeroChassis: e?.numeroChassis || '',
        valeurVenale: e?.valeurVenale ?? '',
        valeurANeuf: e?.valeurANeuf ?? '',
        valeurDeclaree: e?.valeurDeclaree ?? '',
        dateMiseEnCirculation: e?.dateMiseEnCirculation ? dayjs(e.dateMiseEnCirculation) : null,
        carteGrise: null,
        conducteurHabituel: e?.conducteurHabituel || null,
        hasConducteurDifferent: !!e?.conducteurHabituel,
        // Seules les garanties SUPPLÉMENTAIRES (garantie non obligatoire) alimentent la liste ;
        // une garantie à plusieurs sous-garanties n'y figure qu'une fois.
        garantiesSupplementaires: [...new Set((e?.sousGaranties || [])
            .filter((sg) => sg?.garantie?.obligatoire !== true)
            .map((sg) => sg?.garantie?.codeId)
            .filter(Boolean))],
    });

    useEffect(() => {
        setModalIncorporation(props.open);
        if (props.open && props?.contrat?.codeId) {
            console.log("Contrat flotte pour incorporation:", props?.contrat);
            setContratFlotte(props?.contrat);

            // Plancher de date d'effet = dernier AN/RN (début de période), en ignorant les
            // incorporations intermédiaires (sinon des IN successives bloquaient l'antidatage à
            // l'intérieur de la période). Repli sur la date d'effet de l'avenant en cours.
            setPlancherDate(props?.contrat?.dateEffet ? dayjs(props.contrat.dateEffet).startOf('day') : null);
            axiosInstance.get(`/api/contrat/incorporation/plancher/${props.contrat.codeId}`)
                .then((res) => {
                    if (res?.data) setPlancherDate(dayjs(res.data).startOf('day'));
                })
                .catch(() => { /* repli déjà posé sur l'avenant en cours */ });

            // Calculer les jours restants jusqu'à l'échéance
            const dateEcheance = dayjs(props?.contrat?.dateEcheance);
            const jours = dateEcheance.diff((dateEffetIncorporation || dayjs()), 'day');
            setJoursRestants(jours);

            // Charger les données de référence
            loadReferenceData(props?.contrat);

            // Édition d'une proposition IN : précharger les engins déjà incorporés
            // (l'utilisateur peut les corriger / en retirer / en ajouter avant validation).
            if (isEditProposition && Array.isArray(props?.contrat?.incorpores) && props.contrat.incorpores.length) {
                setEnginsData(props.contrat.incorpores.map(mapIncorporeToEnginData));
                if (props?.contrat?.dateEffet) {
                    setDateEffetIncorporation(dayjs(props.contrat.dateEffet));
                }
            }
        }
    }, [props.open, props?.contrat?.codeId]);

    const { PlusCircleOutlined, UserOutlined, CarOutlined, CalendarOutlined, DollarOutlined, InfoCircleOutlined, CheckCircleOutlined, WarningOutlined, DeleteOutlined, CreditCardOutlined, FileExcelOutlined } = require('@ant-design/icons');

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(value);
    };

    const getValeurVenaleMinimum = (engin) => {
        const categorieMin = engin?.categorie && typeof engin.categorie === 'object'
            ? engin.categorie?.valeurVenaleMinimale
            : undefined;

        if (categorieMin !== undefined && categorieMin !== null) {
            const numericCategorieMin = Number(categorieMin);
            if (!Number.isNaN(numericCategorieMin)) {
                return numericCategorieMin;
            }
        }

        const contratCategorieMin = props?.contrat?.engins?.[0]?.categorie?.valeurVenaleMinimale;
        if (contratCategorieMin !== undefined && contratCategorieMin !== null) {
            const numericContratCategorieMin = Number(contratCategorieMin);
            if (!Number.isNaN(numericContratCategorieMin)) {
                return numericContratCategorieMin;
            }
        }

        if (valeurVenaleMinimale !== undefined && valeurVenaleMinimale !== null) {
            const numericValeurVenaleMinimale = Number(valeurVenaleMinimale);
            if (!Number.isNaN(numericValeurVenaleMinimale)) {
                return numericValeurVenaleMinimale;
            }
        }

        return 0;
    };

    const loadReferenceData = async (contrat) => {
        try {
            // Charger les garanties non obligatoires
            const garantiesRes = await axiosInstance.get(`/api/garantie/categorie/non-compulsory/${props?.contrat?.engins[0].categorie.categorieId}/${props?.contrat?.organisme?.code}`);
            setGarantiesNonObligatoires(garantiesRes.data);

            // Charger les professions
            const professionsRes = await axiosInstance.get('/api/parametre/type/PROFESSION');
            setProfessions(professionsRes.data);

            // Charger les frais accessoires
            const fraisRes = await axiosInstance.get(`/api/avenant/accessoires/IN/${props?.contrat?.engins[0]?.categorie?.codeCategorie}`);
            setFraisAccessoires(fraisRes.data);

            const rawValeurMin = contrat?.engins[0]?.categorie?.valeurVenaleMinimale ?? contrat?.engins[0]?.valeurVenale ?? 0;
            const valeurMin = Number(rawValeurMin) || 0;

            setValeurVenaleMinimale(valeurMin);

            // Initialiser la valeur vénale minimale pour le premier engin
            setEnginsData(prev => {
                const newData = [...prev];
                const engin = newData[0];

                if (!engin) {
                    return prev;
                }

                const shouldUpdateField = (fieldValue) => {
                    if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
                        return true;
                    }
                    const numericValue = Number(fieldValue);
                    if (Number.isNaN(numericValue)) {
                        return true;
                    }
                    return numericValue < valeurMin;
                };

                const updatedEngin = { ...engin };
                let hasChanged = false;

                if (shouldUpdateField(updatedEngin.valeurVenale)) {
                    updatedEngin.valeurVenale = valeurMin;
                    hasChanged = true;
                }
                if (shouldUpdateField(updatedEngin.valeurANeuf)) {
                    updatedEngin.valeurANeuf = valeurMin;
                    hasChanged = true;
                }
                if (shouldUpdateField(updatedEngin.valeurDeclaree)) {
                    updatedEngin.valeurDeclaree = valeurMin;
                    hasChanged = true;
                }

                if (!hasChanged) {
                    return prev;
                }

                newData[0] = updatedEngin;
                return newData;
            });
        } catch (error) {
            console.error("Erreur lors du chargement des données:", error);
        }
    };

    // Gestion des engins
    const handleEnginChange = (value, field, index) => {
        setEnginsData(prev => {
            const newData = [...prev];
            const existingEngin = newData[index];

            if (!existingEngin) {
                return prev;
            }

            const updatedEngin = { ...existingEngin };

            // Formater en majuscules pour les champs texte
            let processedValue = value;
            if (typeof value === 'string' && ['matricule', 'numeroSerie', 'marque', 'moteur', 'type', 'numeroChassis'].includes(field)) {
                processedValue = value.toUpperCase();
            }

            // Gestion spéciale pour la valeur vénale
            if (field === 'valeurVenale') {
                const isEmpty = processedValue === null || processedValue === undefined || processedValue === '';
                const oldValeurVenale = updatedEngin.valeurVenale;
                const numericValue = typeof processedValue === 'number' ? processedValue : Number(processedValue);
                const hasNumericValue = !Number.isNaN(numericValue);
                const valeurCourante = isEmpty || !hasNumericValue ? null : numericValue;

                updatedEngin.valeurVenale = valeurCourante;

                if (!isEmpty && hasNumericValue) {
                    const shouldSyncRelatedValue = (fieldValue) => {
                        if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
                            return true;
                        }
                        const numericFieldValue = Number(fieldValue);
                        if (Number.isNaN(numericFieldValue)) {
                            return true;
                        }
                        if (oldValeurVenale === null || oldValeurVenale === undefined || oldValeurVenale === '') {
                            return true;
                        }
                        const numericOld = Number(oldValeurVenale);
                        if (Number.isNaN(numericOld)) {
                            return true;
                        }
                        return numericFieldValue === numericOld;
                    };

                    if (shouldSyncRelatedValue(updatedEngin.valeurANeuf)) {
                        updatedEngin.valeurANeuf = numericValue;
                    }
                    if (shouldSyncRelatedValue(updatedEngin.valeurDeclaree)) {
                        updatedEngin.valeurDeclaree = numericValue;
                    }
                }

                newData[index] = updatedEngin;
                return newData;
            }

            updatedEngin[field] = processedValue;
            newData[index] = updatedEngin;
            return newData;
        });
    };

    const handleValeurVenaleBlur = (index) => {
        setEnginsData(prev => {
            const newData = [...prev];
            const engin = newData[index];

            if (!engin) {
                return prev;
            }

            const minValeur = getValeurVenaleMinimum(engin);
            const isEmpty = engin.valeurVenale === null || engin.valeurVenale === undefined || engin.valeurVenale === '';
            const valeurNumerique = Number(engin.valeurVenale);
            const valeurValide = !Number.isNaN(valeurNumerique);
            const needsAdjustment = isEmpty || !valeurValide || valeurNumerique < minValeur;

            if (!needsAdjustment) {
                return prev;
            }

            toast.warning(`La valeur vénale ne peut être inférieure à ${formatCurrency(minValeur)}`);

            const shouldUpdateRelatedValue = (fieldValue) => {
                if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
                    return true;
                }
                const numericFieldValue = Number(fieldValue);
                if (Number.isNaN(numericFieldValue)) {
                    return true;
                }
                if (valeurValide && numericFieldValue === valeurNumerique) {
                    return true;
                }
                return numericFieldValue < minValeur;
            };

            const updatedEngin = {
                ...engin,
                valeurVenale: minValeur
            };

            if (shouldUpdateRelatedValue(engin.valeurANeuf)) {
                updatedEngin.valeurANeuf = minValeur;
            }
            if (shouldUpdateRelatedValue(engin.valeurDeclaree)) {
                updatedEngin.valeurDeclaree = minValeur;
            }

            newData[index] = updatedEngin;
            return newData;
        });
    };

    const addEngin = () => {
        const valeurMinimale = getValeurVenaleMinimum();
        setEnginsData(prev => [...prev, {
            categorie: 'MOTO',
            matricule: '',
            typeSerie: 'TG',
            numeroSerie: '',
            marque: '',
            energie: 'ESSENCE',
            volume: '',
            nombrePlace: '',
            usage: 'PRIVE',
            moteur: '',
            type: '',
            // Pas de défaut MOTO en dur : null hors moto (jamais « VELOMOTEUR » sur un non-moto).
            genre: null,
            numeroChassis: '',
            valeurVenale: valeurMinimale,
            valeurANeuf: valeurMinimale,
            valeurDeclaree: valeurMinimale,
            dateMiseEnCirculation: null,
            carteGrise: null,
            conducteurHabituel: null,
            hasConducteurDifferent: false,
            garantiesSupplementaires: []
        }]);
    };

    const removeEngin = (index) => {
        if (enginsData.length === 1) {
            toast.error("Vous devez avoir au moins un engin à incorporer");
            return;
        }
        setEnginsData(prev => prev.filter((_, i) => i !== index));
    };

    // ── Import Excel des engins à incorporer ─────────────────────────────────────
    // Le fichier ne contient QUE les nouveaux engins à incorporer (pas de diff avec la
    // flotte existante) : on les ajoute simplement à la liste. Réutilise FlotteExcelImport
    // (modèle identique à l'affaire nouvelle / au renouvellement) — sortie déjà découpée
    // typeSerie/matricule/numeroSerie + dates en dayjs.
    const mapImportedEnginToEnginData = (e) => {
        const valeurMin = getValeurVenaleMinimum();
        const conducteurHabituel = (!e.useSouscripteur && e.personne?.nom) ? e.personne : null;
        return {
            categorie: 'MOTO',
            matricule: e.matricule || '',
            typeSerie: e.typeSerie || '',
            numeroSerie: e.numeroSerie || '',
            marque: e.marque || '',
            energie: e.energie || 'ESSENCE',
            volume: e.volume || e.puissance || '',
            nombrePlace: e.nombrePlace || '',
            usage: e.usage || 'PRIVE',
            moteur: e.moteur || '',
            type: e.type || '',
            genre: e.genre || null,
            numeroChassis: e.numeroChassis || '',
            valeurVenale: e.valeurVenale || valeurMin,
            valeurANeuf: e.valeurANeuf || valeurMin,
            valeurDeclaree: e.valeurDeclaree || valeurMin,
            dateMiseEnCirculation: dayjs.isDayjs(e.dateMiseEnCirculation)
                ? e.dateMiseEnCirculation
                : (e.dateMiseEnCirculation ? dayjs(e.dateMiseEnCirculation) : null),
            carteGrise: null,
            conducteurHabituel,
            hasConducteurDifferent: !!conducteurHabituel,
            garantiesSupplementaires: Array.isArray(e.garantiesSupplementaires) ? e.garantiesSupplementaires : []
        };
    };

    const applyImportedEngins = (importedEngins) => {
        setShowImportModal(false);
        if (!Array.isArray(importedEngins) || importedEngins.length === 0) return;
        // Apparier les garanties supplémentaires du fichier (libellés) aux vraies garanties
        // (codeId) ; les sous-garanties sont résolues à l'aperçu / à l'envoi (avecSousGaranties).
        const mapped = importedEngins.map((e) => ({
            ...mapImportedEnginToEnginData(e),
            garantiesSupplementaires: matchGarantieNamesToIds(
                e.garantiesSupplementaires, garantiesNonObligatoires, (g) => g.nom, (g) => g.codeId),
        }));
        setEnginsData(prev => {
            // Si la seule ligne présente est un engin vierge (saisie non commencée), on la remplace.
            const onlyBlank = prev.length === 1 && !prev[0]?.matricule && !prev[0]?.marque && !prev[0]?.numeroChassis;
            return onlyBlank ? mapped : [...prev, ...mapped];
        });
        toast.success(`${mapped.length} engin(s) importé(s) à incorporer. La prime sera recalculée.`);
    };

    // Gestion du conducteur habituel
    const handleShowConducteurModal = (enginIndex) => {
        const conducteurExistant = enginsData[enginIndex].conducteurHabituel;

        setModalConducteur({
            show: true,
            enginIndex: enginIndex,
            data: conducteurExistant || {
                nom: '',
                prenom: '',
                genre: 'MASCULIN',
                profession: '',
                email: '',
                adresse: '',
                telephone: '',
                typePiece: '',
                numIdentite: '',
                dateDelivrance: ''
            }
        });
    };

    const handleConducteurChange = (e) => {
        const { name, value } = e.target;

        // Formater en majuscules pour les champs texte pertinents
        let processedValue = value;
        if (typeof value === 'string' && ['nom', 'prenom', 'adresse', 'numIdentite'].includes(name)) {
            processedValue = value.toUpperCase();
        }

        setModalConducteur(prev => ({
            ...prev,
            data: {
                ...prev.data,
                [name]: processedValue
            }
        }));
    };

    const saveConducteurHabituel = () => {
        const { enginIndex, data } = modalConducteur;

        if (!data.nom || !data.prenom || !data.adresse || !data.telephone) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }

        setEnginsData(prev => {
            const newData = [...prev];
            newData[enginIndex] = {
                ...newData[enginIndex],
                conducteurHabituel: data,
                hasConducteurDifferent: true
            };
            return newData;
        });

        setModalConducteur({
            show: false,
            enginIndex: null,
            data: {
                nom: '',
                prenom: '',
                genre: 'MASCULIN',
                profession: '',
                email: '',
                adresse: '',
                telephone: '',
                typePiece: '',
                numIdentite: '',
                dateDelivrance: ''
            }
        });

        toast.success('Conducteur habituel enregistré');
    };

    const removeConducteurHabituel = (enginIndex) => {
        setEnginsData(prev => {
            const newData = [...prev];
            newData[enginIndex] = {
                ...newData[enginIndex],
                conducteurHabituel: null,
                hasConducteurDifferent: false
            };
            return newData;
        });
        toast.info('Conducteur habituel retiré - le souscripteur sera utilisé par défaut');
    };

    // Le backend ne lit que engin.sousGaranties (jamais garantiesSupplementaires) : la sélection de
    // l'écran est traduite en sous-garanties juste avant l'aperçu et l'envoi, pour que la prime et
    // l'engin enregistré suivent toujours ce qui est affiché (saisie manuelle, import Excel modifié
    // ensuite, modification d'une proposition). Une erreur remonte : on n'enregistre pas une
    // incorporation privée en silence de ses garanties supplémentaires.
    const avecSousGaranties = (engins) => {
        const cache = {};
        return Promise.all(engins.map(async (engin) => {
            const ids = Array.isArray(engin.garantiesSupplementaires) ? engin.garantiesSupplementaires : [];
            const key = [...ids].sort().join("|");
            if (!key) return { ...engin, sousGaranties: [] };
            if (!cache[key]) {
                const garanties = ids
                    .map((id) => garantiesNonObligatoires.find((g) => g?.codeId === id))
                    .filter(Boolean);
                cache[key] = axiosInstance
                    .post(`/api/sous-garantie/garanties`, JSON.stringify(garanties))
                    .then((res) => res.data || []);
            }
            return { ...engin, sousGaranties: await cache[key] };
        }));
    };

    // Calcul du preview
    const calculatePreview = async () => {
        if (!contratFlotte || enginsData.length === 0) return;

        try {
            const payload = {
                codeId: contratFlotte.codeId,
                dateEffet: dateEffetIncorporation.format('YYYY-MM-DDTHH:mm:ss'),
                engins: enginsData.map(engin => {
                    const conducteurHabituel = normalizeConducteurHabituelPayload(engin.conducteurHabituel);
                    const useSouscripteur = !conducteurHabituel;
                    return {
                        ...engin,
                        conducteurHabituel,
                        hasConducteurDifferent: !useSouscripteur,
                        puissance: engin.volume,
                        immatriculation: (engin.numeroSerie || "") + engin.typeSerie + engin.matricule,
                        categorie: props?.contrat?.engins[0].categorie,
                        dateMiseEnCirculation: engin.dateMiseEnCirculation?.format('YYYY-MM-DD'),
                        useSouscripteur
                    };
                }),
                bonusMalusEdite: bonusMalusPayload,
                membrePersonnel: !!membrePersonnel
            };
            console.log("Payload incorporation:", payload);
            if (!isValidatedEngins()) return;
            payload.engins = await avecSousGaranties(payload.engins);
            const response = await axiosInstance.put(`/api/engin/preview-incorporation`, JSON.stringify(payload));
            console.log("Preview incorporation:", response.data);
            setPreview(response.data);
        } catch (error) {
            console.error("Erreur lors du calcul du preview:", error);
        }
    };

    useEffect(() => {
        if (dateEffetIncorporation && enginsData.length > 0) {
            // Calculer les jours restants jusqu'à l'échéance
            const dateEcheance = dayjs(props?.contrat?.dateEcheance);
            const jours = dateEcheance.diff((dateEffetIncorporation || dayjs()), 'day');
            setJoursRestants(jours);

            const timeoutId = setTimeout(() => calculatePreview(), 500);
            return () => clearTimeout(timeoutId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateEffetIncorporation, enginsData, contratFlotte, bonusMalusEdite, garantiesNonObligatoires]);

    const navigate = useNavigate();

    const enterLoading = () => setLoading(true);
    const outLoading = () => setLoading(false);

    const validateEngins = () => {
        for (let i = 0; i < enginsData.length; i++) {
            const engin = enginsData[i];
            const valeurVenaleManquante = engin.valeurVenale === null || engin.valeurVenale === undefined || engin.valeurVenale === '';
            if (!engin.matricule || !engin.marque || !engin.numeroChassis ||
                !engin.moteur || !engin.type || valeurVenaleManquante ||
                !engin.dateMiseEnCirculation) {
                toast.error(`Veuillez remplir tous les champs obligatoires de l'engin N°${i + 1}`);
                return false;
            }
            const minValeur = getValeurVenaleMinimum(engin);
            const valeurNumerique = Number(engin.valeurVenale);
            if (Number.isNaN(valeurNumerique) || valeurNumerique < minValeur) {
                toast.error(`La valeur vénale de l'engin N°${i + 1} doit être supérieure ou égale à ${formatCurrency(minValeur)}`);
                return false;
            }
        }
        return true;
    };

    const isValidatedEngins = () => {
        for (let i = 0; i < enginsData.length; i++) {
            const engin = enginsData[i];
            const valeurVenaleManquante = engin.valeurVenale === null || engin.valeurVenale === undefined || engin.valeurVenale === '';
            if (!engin.matricule || !engin.marque || !engin.numeroChassis ||
                !engin.moteur || !engin.type || valeurVenaleManquante ||
                !engin.dateMiseEnCirculation) {
                return false;
            }
            const minValeur = getValeurVenaleMinimum(engin);
            const valeurNumerique = Number(engin.valeurVenale);
            if (Number.isNaN(valeurNumerique) || valeurNumerique < minValeur) {
                return false;
            }
        }
        return true;
    };

    const handleConfirmAndSubmit = async () => {
        try {
            await form.validateFields();
        } catch {
            return;
        }

        if (!validateEngins()) return;

        if (!isContratEligible()) {
            toast.error("Ce contrat n'est pas éligible à l'incorporation");
            return;
        }

        const { CarOutlined: CarIcon } = require('@ant-design/icons');

        Modal.confirm({
            title: "Confirmer l'incorporation d'engins",
            width: 520,
            content: (
                <div>
                    <p style={{ marginBottom: "12px" }}>
                        Vous êtes sur le point d'incorporer{" "}
                        <strong>{enginsData.length} engin{enginsData.length > 1 ? "s" : ""}</strong>{" "}
                        au contrat flotte.
                    </p>
                    <div style={{ marginBottom: "12px" }}>
                        <strong>Engins à incorporer :</strong>
                        <ul style={{ margin: "6px 0 0 16px", padding: 0 }}>
                            {enginsData.map((engin, i) => {
                                const immat = `${engin.numeroSerie || ""}${engin.typeSerie}${engin.matricule}`.trim();
                                return (
                                    <li key={i} style={{ marginBottom: "4px" }}>
                                        <strong>{immat || `Engin N°${i + 1}`}</strong>
                                        {engin.marque ? ` — ${engin.marque}` : ""}
                                        {engin.genre ? ` (${engin.genre})` : ""}
                                        {engin.valeurVenale ? ` — VV : ${formatCurrency(engin.valeurVenale)}` : ""}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                    <div style={{ background: "#e6f7ff", padding: "8px 12px", borderRadius: "6px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Date d'effet :</span>
                            <strong>{dateEffetIncorporation?.format("DD/MM/YYYY")}</strong>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
                            <span>Prime totale :</span>
                            <strong>{formatCurrency(preview?.primeTotale || 0)}</strong>
                        </div>
                    </div>
                    <p style={{ marginTop: "12px", color: "#666" }}>
                        Cette action est irréversible. Confirmer ?
                    </p>
                </div>
            ),
            okText: "Confirmer",
            cancelText: "Annuler",
            okType: "primary",
            onOk: handleIncorporationSubmit,
        });
    };

    const handleIncorporationSubmit = async () => {
        try {
            await form.validateFields();

            if (!validateEngins()) return;

            if (!isContratEligible()) {
                toast.error("Ce contrat n'est pas éligible à l'incorporation");
                return;
            }

            enterLoading();

            const payload = {
                codeId: contratFlotte.codeId,
                //Format dateEffet so that it can be accepted by spring boot a LocalDateTime
                dateEffet: dateEffetIncorporation.format('YYYY-MM-DDTHH:mm:ss'),
                engins: enginsData.map(engin => {
                    const conducteurHabituel = normalizeConducteurHabituelPayload(engin.conducteurHabituel);
                    const useSouscripteur = !conducteurHabituel;
                    return {
                        ...engin,
                        conducteurHabituel,
                        hasConducteurDifferent: !useSouscripteur,
                        puissance: engin.volume,
                        immatriculation: (engin.numeroSerie || "") + engin.typeSerie + engin.matricule,
                        categorie: props?.contrat?.engins[0].categorie,
                        dateMiseEnCirculation: engin.dateMiseEnCirculation?.format('YYYY-MM-DD'),
                        useSouscripteur
                    };
                }),
                primeNette: preview?.primeNette || 0,
                accessoires: fraisAccessoires,
                taxes: preview?.taxes || 0,
                primeTotale: preview?.primeTotale || 0,
                bonusMalusEdite: bonusMalusPayload,
                membrePersonnel: !!membrePersonnel
            };
            payload.engins = await avecSousGaranties(payload.engins);

            console.log("Payload incorporation:", payload);

            // Édition d'une proposition existante (IN + EN_PROPOSITION) → modification EN PLACE
            // (remplace les engins incorporés). Sinon → création d'incorporation.
            const endpoint = isEditProposition
                ? `/api/engin/incorporation/proposition`
                : `/api/engin/incorporation`;
            const response = await axiosInstance.put(endpoint, JSON.stringify(payload));

            await applyAvenantAgenceCommission(response.data?.codeId, avCom.agence, avCom.taux, avCom.intermediaire);
            toast.success(
                isEditProposition
                    ? "Proposition d'incorporation modifiée avec succès!"
                    : "Incorporation effectuée avec succès!"
            );
            setModalIncorporation(false);
            props.update(false);
            navigate(`/details-contrat/${response.data.codeId}`, { replace: true });
            window.location.reload();

        } catch (error) {
            console.error(error);
            toast.error("Erreur lors de l'incorporation");
        } finally {
            outLoading();
        }
    };

    const handleCancel = () => {
        setModalIncorporation(false);
        props.update(false);
    };

    const isContratEligible = () => {
        return contratFlotte?.engins && contratFlotte?.engins?.length &&
            joursRestants > 0;
    };

    return (
        <>
            <SDIModal
                title="INCORPORATION D'ENGINS"
                centered
                confirmLoading={loading}
                open={modalIncorporation}
                onOk={handleConfirmAndSubmit}
                onCancel={handleCancel}
                width={1200}
                zIndex={1940}
                okText="Incorporer les engins"
                cancelText="Annuler"
            >
                <ConfigProvider locale={frFR}>
                    <div className="incorporation-content">
                        <AvenantAgencySelector
                            organismeCode={props?.contrat?.organisme?.code}
                            defaultAgence={props?.contrat?.courtier || props?.contrat?.producteur?.agence || props?.contrat?.intermediaire?.agence}
                            defaultIntermediaire={props?.contrat?.intermediaire}
                            categorie={props?.contrat?.engins?.[0]?.categorie?.codeCategorie}
                            onChange={(ag, t, inter) => setAvCom({ agence: ag, taux: t, intermediaire: inter })}
                        />

                        {/* Informations sur le contrat flotte */}
                        {contratFlotte && (
                            <Card className="mb-4" size="small" title="Contrat flotte">
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Statistic
                                            title="Numéro de police"
                                            value={contratFlotte.numeroPoliceP || contratFlotte.numeroPoliceM}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic
                                            title="Jours restants"
                                            value={`${joursRestants} jours`}
                                            valueStyle={{ color: joursRestants > 0 ? '#3f8600' : '#cf1322' }}
                                            prefix={<CalendarOutlined />}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic
                                            title="Date d'échéance"
                                            value={dayjs(contratFlotte.dateEcheance).format('DD/MM/YYYY')}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        )}

                        {/* Conditions d'éligibilité */}
                        {!isContratEligible() ? (
                            <Alert
                                message="Contrat non éligible"
                                description="L'incorporation n'est possible que sur les contrats flotte actifs avec des jours restants."
                                type="error"
                                icon={<WarningOutlined />}
                                showIcon
                                className="mb-4"
                            />
                        ) : null}

                        {/* Règles d'incorporation */}
                        {/* <Card className="mb-4" size="small" title="Règles d'incorporation">
                            <div className="info-section">
                                <div className="info-item">
                                    <InfoCircleOutlined className="info-icon info" />
                                    <Text>
                                        <Text strong>Code avenant :</Text> AI + Année + 001
                                    </Text>
                                </div>
                                <div className="info-item">
                                    <CarOutlined className="info-icon warning" />
                                    <Text>
                                        <Text strong>Catégorie :</Text> Uniquement engins à 2 ou 3 roues
                                    </Text>
                                </div>
                                <div className="info-item">
                                    <CalendarOutlined className="info-icon success" />
                                    <Text>
                                        <Text strong>Prime :</Text> Calculée au prorata des jours restants
                                    </Text>
                                </div>
                            </div>
                        </Card> */}

                        <Form form={form} layout="vertical">
                            {/* Date d'effet */}
                            <Form.Item
                                label="Date d'effet de l'incorporation"
                                name="dateEffet"
                                rules={[{ required: true, message: 'Veuillez sélectionner une date' }]}
                                initialValue={dateEffetIncorporation}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    value={dateEffetIncorporation}
                                    onChange={setDateEffetIncorporation}
                                    disabledDate={(current) => {
                                        if (!current) return false;
                                        const dateEcheance = contratFlotte ? dayjs(contratFlotte.dateEcheance) : dayjs().add(1, 'year');
                                        // Après l'échéance : toujours interdit.
                                        if (current > dateEcheance) return true;
                                        // Plancher : jamais avant le début de la période (dernier AN/RN),
                                        // et non la dernière incorporation. Repli sur l'avenant en cours.
                                        const plancher = plancherDate
                                            || (contratFlotte?.dateEffet ? dayjs(contratFlotte.dateEffet).startOf('day') : null);
                                        if (plancher && current < plancher) return true;
                                        // Antidatage (date passée) : autorisé uniquement sous HCO081.
                                        if (!canAntidate && current < dayjs().startOf('day')) return true;
                                        return false;
                                    }}
                                />
                            </Form.Item>

                            {canMarkPersonnel && (
                                <div style={{ marginBottom: 12 }}>
                                    <MembrePersonnelCheck checked={membrePersonnel} onToggle={handleMembrePersonnel} />
                                </div>
                            )}
                            {/* Ajustement bonus/malus (HCO080, ou flux membre du personnel HCO090) — opt-in */}
                            {(canEditBonus || membrePersonnel) && (
                                <Card size="small" title="Ajustement Bonus / Malus" style={{ marginBottom: 12 }}>
                                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                                        Bonus = réduction (max {effectiveBonusMax} %), Malus = majoration. Laissez à 0 pour ne pas modifier.
                                    </Text>
                                    <Space.Compact style={{ width: "100%" }}>
                                        <Segmented
                                            value={bonusMalusSens}
                                            onChange={(sens) => applyBonusSens(sens, bonusMalusAbs)}
                                            options={[
                                                { label: "Bonus", value: "BONUS" },
                                                { label: "Malus", value: "MALUS" },
                                            ]}
                                        />
                                        <InputNumber
                                            style={{ flex: 1 }}
                                            min={0}
                                            max={bonusMalusSens === "BONUS" ? effectiveBonusMax : 100}
                                            step={1}
                                            addonAfter="%"
                                            value={bonusMalusAbs}
                                            placeholder="0"
                                            onChange={(v) => applyBonusSens(bonusMalusSens, v)}
                                        />
                                    </Space.Compact>
                                </Card>
                            )}

                            {/* Liste des engins à incorporer */}
                            <Title level={5} className="mb-3">Engins à incorporer</Title>

                            {enginsData.map((engin, index) => (
                                <Card
                                    key={index}
                                    className="mb-3"
                                    size="small"
                                    title={`Engin N°${index + 1}`}
                                    extra={
                                        enginsData.length > 1 && (
                                            <Button
                                                danger
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                onClick={() => removeEngin(index)}
                                            >
                                                Supprimer
                                            </Button>
                                        )
                                    }
                                >
                                    <Row gutter={16}>
                                        {/* Immatriculation */}
                                        <Col span={8}>
                                            <Form.Item label="Immatriculation *">
                                                <Input.Group compact>
                                                    <Select
                                                        value={engin.typeSerie}
                                                        onChange={(val) => handleEnginChange(val, 'typeSerie', index)}
                                                        style={{ width: '20%' }}
                                                    >
                                                        <Option value="TG">TG</Option>
                                                        <Option value="Autre">Autre</Option>
                                                    </Select>
                                                    <Input
                                                        value={engin.matricule}
                                                        onChange={(e) => handleEnginChange(e.target.value, 'matricule', index)}
                                                        style={{ width: '50%' }}
                                                        placeholder="Immatriculation"
                                                    />
                                                    {engin.typeSerie === 'TG' && (
                                                        <Input
                                                            value={engin.numeroSerie}
                                                            onChange={(e) => handleEnginChange(e.target.value, 'numeroSerie', index)}
                                                            style={{ width: '30%' }}
                                                            placeholder="N° série"
                                                        />
                                                    )}
                                                </Input.Group>
                                            </Form.Item>
                                        </Col>

                                        {/* Marque */}
                                        <Col span={8}>
                                            <Form.Item label="Marque *">
                                                <MarqueSelect
                                                    marques={marques}
                                                    value={engin.marque}
                                                    onChange={(val) => {
                                                        handleEnginChange(val, 'marque', index);
                                                        handleEnginChange("", 'type', index); // les modèles dépendent de la marque
                                                    }}
                                                    style={{ width: '100%' }}
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Énergie */}
                                        <Col span={8}>
                                            <Form.Item label="Énergie *">
                                                <Select
                                                    value={engin.energie}
                                                    onChange={(val) => handleEnginChange(val, 'energie', index)}
                                                >
                                                    <Option value="ESSENCE">ESSENCE</Option>
                                                    <Option value="DIESEL">DIESEL</Option>
                                                    <Option value="ELECTRIQUE">ÉLECTRIQUE</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>

                                        {/* Usage */}
                                        <Col span={8}>
                                            <Form.Item label="Usage *">
                                                <Select
                                                    value={engin.usage}
                                                    onChange={(val) => handleEnginChange(val, 'usage', index)}
                                                >
                                                    <Option value="PRIVE">PRIVE</Option>
                                                    <Option value="TAXI">TAXI</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>

                                        {/* Volume */}
                                        <Col span={8}>
                                            <Form.Item label="Volume (cm³) *">
                                                <InputNumber
                                                    value={engin.volume}
                                                    onChange={(val) => handleEnginChange(val, 'volume', index)}
                                                    style={{ width: '100%' }}
                                                    placeholder="Volume"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Nombre de places */}
                                        <Col span={8}>
                                            <Form.Item label="Nombre de places *">
                                                <InputNumber
                                                    value={engin.nombrePlace}
                                                    onChange={(val) => handleEnginChange(val, 'nombrePlace', index)}
                                                    style={{ width: '100%' }}
                                                    placeholder="Nombre de places"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Genre */}
                                        <Col span={8}>
                                            <Form.Item label="Genre *">
                                                <Select
                                                    showSearch
                                                    optionFilterProp="label"
                                                    value={engin.genre}
                                                    onChange={(val) => handleEnginChange(val, 'genre', index)}
                                                    options={genres?.map((g) => ({ label: g.libelleParametre, value: g.libelleParametre }))}
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Carrosserie — champ absent jusqu'ici sur l'incorporation */}
                                        <Col span={8}>
                                            <Form.Item label="Carrosserie">
                                                <Select
                                                    showSearch
                                                    allowClear
                                                    optionFilterProp="label"
                                                    placeholder="Sélectionner la carrosserie"
                                                    value={engin.carosserie}
                                                    onChange={(val) => handleEnginChange(val, 'carosserie', index)}
                                                    options={carosseries?.map((c) => ({ label: c.libelleParametre, value: c.libelleParametre }))}
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* N° Moteur */}
                                        <Col span={8}>
                                            <Form.Item label="N° Moteur *">
                                                <Input
                                                    value={engin.moteur}
                                                    onChange={(e) => handleEnginChange(e.target.value, 'moteur', index)}
                                                    placeholder="Numéro de moteur"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Type */}
                                        <Col span={8}>
                                            <Form.Item label="Type *">
                                                <TypeAutoComplete
                                                    marques={marques}
                                                    marque={engin.marque}
                                                    value={engin.type}
                                                    onChange={(val) => handleEnginChange(val, 'type', index)}
                                                    style={{ width: '100%' }}
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* N° Châssis */}
                                        <Col span={8}>
                                            <Form.Item label="N° Châssis *">
                                                <Input
                                                    value={engin.numeroChassis}
                                                    onChange={(e) => handleEnginChange(e.target.value, 'numeroChassis', index)}
                                                    placeholder="Numéro de châssis"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Valeur vénale */}
                                        <Col span={8}>
                                            <Form.Item label="Valeur vénale *">
                                                <InputNumber
                                                    value={engin.valeurVenale}
                                                    onChange={(val) => handleEnginChange(val, 'valeurVenale', index)}
                                                    onBlur={() => handleValeurVenaleBlur(index)}
                                                    style={{ width: '100%' }}
                                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                                    parser={(value) => value.replace(/\s/g, '')}
                                                    placeholder="Valeur vénale"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Valeur déclarée */}
                                        <Col span={8}>
                                            <Form.Item label="Valeur déclarée *">
                                                <InputNumber
                                                    value={engin.valeurDeclaree}
                                                    onChange={(val) => handleEnginChange(val, 'valeurDeclaree', index)}
                                                    style={{ width: '100%' }}
                                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                                    parser={(value) => value.replace(/\s/g, '')}
                                                    placeholder="Valeur Déclarée"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Valeur à neuf */}
                                        <Col span={8}>
                                            <Form.Item label="Valeur à neuf *">
                                                <InputNumber
                                                    value={engin.valeurANeuf}
                                                    onChange={(val) => handleEnginChange(val, 'valeurANeuf', index)}
                                                    style={{ width: '100%' }}
                                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                                    parser={(value) => value.replace(/\s/g, '')}
                                                    placeholder="Valeur à neuf"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Date de mise en circulation */}
                                        <Col span={8}>
                                            <Form.Item label="Année de mise en circulation *">
                                                <DatePicker
                                                    value={engin.dateMiseEnCirculation}
                                                    onChange={(date) => handleEnginChange(date, 'dateMiseEnCirculation', index)}
                                                    picker="year"
                                                    style={{ width: '100%' }}
                                                    placeholder="Sélectionner l'année"
                                                    disabledDate={(current) => current && current > dayjs()}
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Garanties supplémentaires */}
                                        <Col span={8}>
                                            <Form.Item label="Garanties supplémentaires">
                                                <Select
                                                    mode="multiple"
                                                    value={engin.garantiesSupplementaires}
                                                    onChange={(val) => handleEnginChange(val, 'garantiesSupplementaires', index)}
                                                    placeholder="Sélectionner les garanties"
                                                >
                                                    {garantiesNonObligatoires.map((gar, id) => (
                                                        <Option key={id} value={gar.codeId}>
                                                            {gar.nom}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>

                                        {/* Conducteur habituel */}
                                        <Col span={24}>
                                            {engin.hasConducteurDifferent ? (
                                                <Card size="small" className="bg-light">
                                                    <Row justify="space-between" align="middle">
                                                        <Col>
                                                            <Text strong>Conducteur habituel : </Text>
                                                            <Text>
                                                                {engin.conducteurHabituel?.nom} {engin.conducteurHabituel?.prenom}
                                                            </Text>
                                                        </Col>
                                                        <Col>
                                                            <Button
                                                                size="small"
                                                                onClick={() => handleShowConducteurModal(index)}
                                                                className="me-2"
                                                            >
                                                                Modifier
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                danger
                                                                onClick={() => removeConducteurHabituel(index)}
                                                            >
                                                                Retirer
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </Card>
                                            ) : (
                                                <Alert
                                                    message="Par défaut, le souscripteur du contrat sera le conducteur habituel"
                                                    type="info"
                                                    action={
                                                        <Button
                                                            size="small"
                                                            type="primary"
                                                            icon={<UserOutlined />}
                                                            onClick={() => handleShowConducteurModal(index)}
                                                        >
                                                            Préciser un conducteur différent
                                                        </Button>
                                                    }
                                                />
                                            )}
                                        </Col>
                                    </Row>
                                </Card>
                            ))}

                            {/* Import Excel : uniquement les nouveaux engins à incorporer */}
                            <Button
                                type="default"
                                onClick={() => setShowImportModal(true)}
                                icon={<FileExcelOutlined />}
                                block
                                className="mb-2"
                            >
                                Importer des engins (Excel)
                            </Button>

                            {/* Bouton pour ajouter un engin */}
                            <Button
                                type="dashed"
                                onClick={addEngin}
                                icon={<PlusCircleOutlined />}
                                block
                                className="mb-4"
                            >
                                Ajouter un autre engin
                            </Button>

                            {/* Récapitulatif financier */}
                            {preview && (
                                <Card className="cost-summary" size="small">
                                    <Title level={5}>
                                        <DollarOutlined className="mr-2" />
                                        Récapitulatif financier
                                    </Title>
                                    <div className="cost-breakdown">
                                        {preview.primeNetteAnnuelle != null && (
                                            <Row justify="space-between" className="cost-line">
                                                <Col>
                                                    <Text type="secondary">Prime nette annuelle (100%):</Text>
                                                </Col>
                                                <Col>
                                                    <Text type="secondary">{formatCurrency(preview.primeNetteAnnuelle || 0)}</Text>
                                                </Col>
                                            </Row>
                                        )}
                                        {/* Réductions réellement appliquées aux engins incorporés (flotte + BNS). */}
                                        {preview.flotte != null && preview.flotte !== 0 && (
                                            <Row justify="space-between" className="cost-line">
                                                <Col>
                                                    <Text type="secondary">Réduction flotte (engins incorporés):</Text>
                                                </Col>
                                                <Col>
                                                    <Text type="secondary" style={{ color: "#52c41a" }}>− {preview.flotte}%</Text>
                                                </Col>
                                            </Row>
                                        )}
                                        {preview.bonusMalus != null && preview.bonusMalus !== 0 && (
                                            <Row justify="space-between" className="cost-line">
                                                <Col>
                                                    <Text type="secondary">
                                                        {preview.bonusMalus > 0 ? "Bonus appliqué" : "Malus appliqué"} :
                                                    </Text>
                                                </Col>
                                                <Col>
                                                    <Text type="secondary" style={{ color: preview.bonusMalus > 0 ? "#52c41a" : "#cf1322" }}>
                                                        {preview.bonusMalus > 0 ? "− " : "+ "}{Math.abs(preview.bonusMalus)}%
                                                    </Text>
                                                </Col>
                                            </Row>
                                        )}
                                        {preview.prorata != null && (
                                            <Row justify="space-between" className="cost-line">
                                                <Col>
                                                    <Text type="secondary">
                                                        Prorata : {Math.round((preview.prorata || 0) * 1000) / 10}% ({joursRestants} jours restants)
                                                    </Text>
                                                </Col>
                                                <Col />
                                            </Row>
                                        )}
                                        <Row justify="space-between" className="cost-line">
                                            <Col>
                                                <Text>Prime nette au prorata ({joursRestants} jours):</Text>
                                            </Col>
                                            <Col>
                                                <Text strong>{formatCurrency(preview.primeNette || 0)}</Text>
                                            </Col>
                                        </Row>

                                        <Row justify="space-between" className="cost-line">
                                            <Col>
                                                <Text>Frais accessoires:</Text>
                                            </Col>
                                            <Col>
                                                <Text>{formatCurrency(preview.accessoires || 0)}</Text>
                                            </Col>
                                        </Row>

                                        <Row justify="space-between" className="cost-line">
                                            <Col>
                                                <Text>Taxes (6%):</Text>
                                            </Col>
                                            <Col>
                                                <Text>{formatCurrency(preview.taxes || 0)}</Text>
                                            </Col>
                                        </Row>

                                        <Divider className="my-2" />

                                        <Row justify="space-between" className="cost-line total-cost">
                                            <Col>
                                                <Text strong className="text-lg">PRIME TOTALE:</Text>
                                            </Col>
                                            <Col>
                                                <Text strong className="text-lg text-blue-600">
                                                    {formatCurrency(preview.primeTotale || 0)}
                                                </Text>
                                            </Col>
                                        </Row>
                                    </div>
                                </Card>
                            )}
                        </Form>
                    </div>
                </ConfigProvider>

                <style jsx>
                    {`
                        .incorporation-content {
                            max-height: 70vh;
                            overflow-y: auto;
                        }
                        
                        .info-section {
                            display: flex;
                            flex-direction: column;
                            gap: 12px;
                        }
                        
                        .info-item {
                            display: flex;
                            align-items: flex-start;
                            gap: 8px;
                        }
                        
                        .info-icon {
                            margin-top: 2px;
                            font-size: 16px;
                        }
                        
                        .info-icon.warning {
                            color: #faad14;
                        }
                        
                        .info-icon.info {
                            color: #1890ff;
                        }
                        
                        .info-icon.success {
                            color: #52c41a;
                        }
                        
                        .cost-summary {
                            background-color: #f8f9fa;
                            border: 1px solid #e9ecef;
                        }
                        
                        .cost-breakdown .cost-line {
                            margin-bottom: 8px;
                        }
                        
                        .cost-breakdown .total-cost {
                            background-color: #e6f7ff;
                            padding: 8px;
                            border-radius: 4px;
                            margin-top: 8px;
                        }
                        
                        .bg-light {
                            background-color: #f5f5f5;
                        }
                        
                        .mr-2 { margin-right: 8px; }
                        .me-2 { margin-right: 8px; }
                        .mb-3 { margin-bottom: 12px; }
                        .mb-4 { margin-bottom: 16px; }
                        .my-2 { margin: 8px 0; }
                        .text-lg { font-size: 16px; }
                        .text-blue-600 { color: #1890ff; }
                    `}
                </style>
            </SDIModal>
            {/* Modal pour le conducteur habituel */}
            <SDIModal
                title={`Conducteur habituel - Engin N°${modalConducteur.enginIndex !== null ? modalConducteur.enginIndex + 1 : ''}`}
                open={modalConducteur.show}
                onOk={saveConducteurHabituel}
                onCancel={() => setModalConducteur(prev => ({ ...prev, show: false }))}
                width={800}
                okText="Enregistrer"
                cancelText="Annuler"
                zIndex={2000}
            >
                <ConfigProvider locale={frFR}>
                    <div className="text-center mb-4">
                        <Text type="secondary">
                            Précisez le conducteur habituel si différent du souscripteur du contrat
                        </Text>
                    </div>

                    <Form layout="vertical">
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Nom *">
                                    <Input
                                        name="nom"
                                        value={modalConducteur.data.nom}
                                        onChange={handleConducteurChange}
                                        placeholder="Nom du conducteur"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Prénom(s) *">
                                    <Input
                                        name="prenom"
                                        value={modalConducteur.data.prenom}
                                        onChange={handleConducteurChange}
                                        placeholder="Prénom(s) du conducteur"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Sexe *">
                                    <Select
                                        name="genre"
                                        value={modalConducteur.data.genre}
                                        onChange={(value) => handleConducteurChange({ target: { name: 'genre', value } })}
                                    >
                                        <Option value="MASCULIN">MASCULIN</Option>
                                        <Option value="FEMININ">FEMININ</Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Profession">
                                    <Select
                                        name="profession"
                                        value={modalConducteur.data.profession}
                                        onChange={(value) => handleConducteurChange({ target: { name: 'profession', value } })}
                                        placeholder="Sélectionner la profession"
                                        showSearch
                                    >
                                        <Option value="">Aucune</Option>
                                        {professions.map((prof, id) => (
                                            <Option key={id} value={prof.libelleParametre}>
                                                {prof.libelleParametre}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Téléphone *">
                                    <Input
                                        name="telephone"
                                        value={modalConducteur.data.telephone}
                                        onChange={handleConducteurChange}
                                        placeholder="00000000"
                                        addonBefore="TG (+228)"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Email">
                                    <Input
                                        name="email"
                                        type="email"
                                        value={modalConducteur.data.email}
                                        onChange={handleConducteurChange}
                                        placeholder="email@exemple.com"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Adresse *">
                                    <Input
                                        name="adresse"
                                        value={modalConducteur.data.adresse}
                                        onChange={handleConducteurChange}
                                        placeholder="Adresse de résidence"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Type de pièce">
                                    <Select
                                        name="typePiece"
                                        value={modalConducteur.data.typePiece}
                                        onChange={(value) => handleConducteurChange({ target: { name: 'typePiece', value } })}
                                        placeholder="Sélectionner le type"
                                    >
                                        <Option value="">Aucune</Option>
                                        <Option value="CNI">CNI</Option>
                                        <Option value="PASSEPORT">PASSEPORT</Option>
                                        <Option value="PERMIS">PERMIS DE CONDUIRE</Option>
                                        <Option value="ELECTEUR">CARTE D'ÉLECTEUR</Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Numéro de la pièce">
                                    <Input
                                        name="numIdentite"
                                        value={modalConducteur.data.numIdentite}
                                        onChange={handleConducteurChange}
                                        placeholder="Numéro de la pièce"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Date de délivrance">
                                    <Input
                                        type="date"
                                        name="dateDelivrance"
                                        value={modalConducteur.data.dateDelivrance}
                                        onChange={handleConducteurChange}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </ConfigProvider>
            </SDIModal>

            {/* Import Excel des engins à incorporer — uniquement les nouveaux engins.
                zIndex > 2000 pour passer au-dessus du modal d'incorporation. */}
            <FlotteExcelImport
                visible={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImportSuccess={applyImportedEngins}
                marques={marques}
                carosseries={carosseries}
                categorie={props?.contrat?.engins?.[0]?.categorie?.codeCategorie || props?.contrat?.engins?.[0]?.categorie}
                valeurParDefaut={250000}
                zIndex={2100}
            />
        </>
    );
}

Incorporation.propTypes = {
    open: PropTypes.bool,
    update: PropTypes.func,
    contrat: PropTypes.object
};

export default Incorporation;
